import { FC, useState } from 'react';
import GenericTemplate from '../../../template/GenericTemplate';
import React from 'react';
import { Container } from '@mui/material';
import Typography from '@mui/material/Typography/Typography';
import Grid from '@mui/material/Grid/Grid';
import SelectFormControl from '../../../parts/SelectFormControl';
import { SelectValue } from '../../../../types/SelectValue';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import FormControl from '@mui/material/FormControl/FormControl';
import Button from '@mui/material/Button/Button';
import Box from '@mui/material/Box/Box';
import TableContainer from '@mui/material/TableContainer/TableContainer';
import Table from '@mui/material/Table/Table';
import TableBody from '@mui/material/TableBody/TableBody';
import TableRow from '@mui/material/TableRow/TableRow';
import TableCell from '@mui/material/TableCell/TableCell';
import TableHead from '@mui/material/TableHead/TableHead';
import TablePagination from '@mui/material/TablePagination/TablePagination';
import { Prefecture, Area, FindAreaListRequest } from '../../../../API';
import Radio from '@mui/material/Radio/Radio';
import RadioGroup from '@mui/material/RadioGroup/RadioGroup';
import { ErrorData } from './ReserveAreaListErrorData';
import FormHelperText from '@mui/material/FormHelperText';
import AlertDialog from '../../alert';
import BackToTopButton from 'views/parts/BackToTop';

const styles = {
  searchField: {
    '&.MuiContainer-root': {
      '@media screen and (min-width: 1200px)': {
        maxWidth: '1400px',
      },
    },
  },

  label: {
    margin: 'auto',
    textAlign: 'right',
    pr: '10px',
    mt: '7px',
  },

  categoryTitle: {
    pl: '10px',
    borderLeft: '5px solid #0058a0',
  },

  inputItem: {
    ph: '2px',
    boxSizing: 'border-box',
    width: '100%',
    '& > div > input': {
      maxHeight: '10px',
    },
  },

  button: {
    minWidth: '120px',
    fontWeight: 'bold',
    mt: 1,
    ml: 1,
  },

  listButton: {
    minWidth: '80px',
    fontWeight: 'bold',
    m: 0,
  },

  categoryBlock: {
    mt: '5px',
    px: '10px',
    mb: '5px',
  },

  deleteCell: {
    '& > a': {
      textDecoration: 'none',
    },
  },
};

export interface Props {
  prefectureCode: string;
  prefectureList: Prefecture[];
  inputData: FindAreaListRequest;
  resultData: Area[];
  errorAttribute: ErrorData;
  alertOpen: boolean;
  alertContent: string;
  page: number;
  rowsPerPage: number;
  handleChangePrefectureCode: (_: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeInput: (_: React.ChangeEvent<HTMLInputElement>) => void;
  handleClickSearch: () => void;
  handleClickDeleteButton: (_: Area) => void;
  handleClickCreateButton: () => void;
  handleClickDetailButton: (_: Area) => void;
  handleAlertClose: (_: boolean) => void;
  handleChangePage: (_: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => void;
  handleChangeRowsPerPage: (_: React.ChangeEvent<HTMLInputElement>) => void;
}

const ReserveAreaList: FC<Props> = (props) => {
  return (
    <GenericTemplate title="予約枠（地区）一覧">
      <div>
        <BackToTopButton />
      </div>
      <Container sx={styles.searchField}>
        <Typography variant="h6" gutterBottom component="div" sx={styles.categoryTitle}>
          検索条件
        </Typography>
        <Grid container spacing={0.5} sx={styles.categoryBlock}>
          <Grid item xs={3}>
            <Typography sx={styles.label}>都道府県</Typography>
          </Grid>
          <Grid item xs={7}>
            <SelectFormControl
              value={props.prefectureCode}
              selectName="PrefectureCode"
              selectValues={props.prefectureList.map((prefecture, idx) => {
                return { value: prefecture.PrefectureCode, displayName: prefecture.PrefectureName } as SelectValue;
              })}
              setCondition={props.handleChangePrefectureCode}
              sx={styles.inputItem}
            />
          </Grid>
        </Grid>
        <Grid container spacing={0.5} sx={styles.categoryBlock}>
          <Grid item xs={3}>
            <Typography sx={styles.label}>有効区分</Typography>
          </Grid>
          <Grid item xs={7}>
            <RadioGroup row aria-label="position" defaultValue="1">
              <FormControlLabel
                value="1"
                control={<Radio color="primary" name="IsActive" value={'1'} onChange={props.handleChangeInput} />}
                label="有効"
              />
              <FormControlLabel
                value="0"
                control={<Radio color="primary" name="IsActive" value={'0'} onChange={props.handleChangeInput} />}
                label="無効"
              />
            </RadioGroup>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'center', '& > a': { textDecoration: 'none' } }}>
          <Button variant="outlined" sx={styles.button} onClick={props.handleClickCreateButton}>
            新規作成
          </Button>
          <Button variant="contained" sx={styles.button} onClick={props.handleClickSearch}>
            検索
          </Button>
        </Box>
      </Container>
      {props.errorAttribute.Header.ErrorMessage && (
        <Grid container spacing={2} sx={{ pl: 0, pb: 2 }}>
          <Grid item xs={12}>
            <FormControl component="fieldset" error={props.errorAttribute.Header.IsError}>
              <FormHelperText sx={{ ml: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>
                {props.errorAttribute.Header.ErrorMessage}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      )}
      <Container sx={styles.searchField}>
        <Typography variant="h6" gutterBottom component="div" sx={styles.categoryTitle}>
          地区一覧
        </Typography>
        <TableContainer sx={{ minHeight: 300 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>地区ID</TableCell>
                <TableCell>地区名</TableCell>
                <TableCell>受入人数</TableCell>
                <TableCell>同時受入校数</TableCell>
                <TableCell>同時受入人数</TableCell>
                <TableCell>開始日</TableCell>
                <TableCell>終了日</TableCell>
                <TableCell>表示順</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.resultData
                .slice(props.page * props.rowsPerPage, props.page * props.rowsPerPage + props.rowsPerPage)
                .map((area, index) => (
                  <TableRow key={index}>
                    <TableCell component="td" scope="row">
                      <Button
                        variant="contained"
                        sx={styles.listButton}
                        size="small"
                        onClick={() => props.handleClickDeleteButton(area)}
                      >
                        削除
                      </Button>
                    </TableCell>
                    <TableCell component="td" scope="row">
                      {area.AreaId}
                    </TableCell>
                    <TableCell component="td" scope="row">
                      {area.AreaName}
                    </TableCell>
                    <TableCell component="td" scope="row">
                      {area.MaxGuestCount}
                    </TableCell>
                    <TableCell component="td" scope="row">
                      {area.MaxSchoolCount}
                    </TableCell>
                    <TableCell component="td" scope="row">
                      {area.MaxMultiSchoolGuestCount}
                    </TableCell>
                    <TableCell component="td" scope="row">
                      {area.FromDate}
                    </TableCell>
                    <TableCell component="td" scope="row">
                      {area.ToDate}
                    </TableCell>
                    <TableCell component="td" scope="row">
                      {area.DisplayOrder}
                    </TableCell>
                    <TableCell component="td" scope="row" sx={styles.deleteCell}>
                      <Button
                        variant="contained"
                        sx={styles.listButton}
                        size="small"
                        onClick={() => props.handleClickDetailButton(area)}
                      >
                        詳細
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 25, 100]}
          component="div"
          count={props.resultData.length}
          rowsPerPage={props.rowsPerPage}
          page={props.page}
          onPageChange={props.handleChangePage}
          onRowsPerPageChange={props.handleChangeRowsPerPage}
        />
        <AlertDialog
          handleDialogClose={props.handleAlertClose}
          dialogOpen={props.alertOpen}
          content={props.alertContent}
        />
      </Container>
    </GenericTemplate>
  );
};

export default ReserveAreaList;
